'use strict';

import VZSpinner from 'Components/VZSpinner';
import * as debounce from 'lodash/debounce';
import { appendParamToURL, getParamFromURL, getUrlFromAttributes, removeParamFromURL } from 'Util/urlUtils';
import Alert from 'Components/Alert';
import Animation from '../../Animation';
import { scrollToElement } from 'Util/domUtils';
import VZConfirmationModal from '../../ConfirmationModal';
import { bindQuantityButtons, parseQuantity, toggleQuantityButtons } from './quantitySelectorUtils';

const PRODUCT_CARD_SELECTORS = 'vz-product-card, vz-tc-product-card';

export default class VZQuantitySelector extends HTMLElement {
    constructor() {
        super();
        this.excludedAttributes = ['class', 'data-configurator-type', 'data-url', 'data-general-error'];

        this.onInput = this.onInput.bind(this);
        this.toggleIncreaseAndDecreaseButtons = this.toggleIncreaseAndDecreaseButtons.bind(this);
        this.confirmRemove = this.getAttribute('data-confirm-remove') === 'true';
    }

    connectedCallback() {
        this.spinner = document.querySelector('vz-spinner') || new VZSpinner();
        this.sidebar = document.querySelector('.js-configurator-sidebar');
        this.editMode = document.querySelector('.js-edit-mode');

        // reset ID to ensure unique selector and native label feature still works
        this.setAttribute('id', Date.now());

        this.forbiddenValues = ['e', '+', ',', '-', '.', 69, 187, 188, 189, 190];
        this.debounceRate = parseFloat(this.getAttribute('debounce-rate')) || 500;
        this.debouncedChange = debounce(this.onChange.bind(this), this.debounceRate);
        this.quantityField = this.querySelector('.js-quantity-field');
        this.formControl = this.querySelector('.form-control');
        this.quantityFieldOrgValue = this.quantityField.value;
        this.productQuantityField = document.querySelector('.js-quantity-selector');

        if (this.quantityField.value !== '0') {
            this.formControl.classList.add('border-turquoise');
        }

        this.quantityField.addEventListener('blur', this.onBlur.bind(this));
        this.addEventListener('input', this.onInput.bind(this));
        this.addEventListener('keydown', this.onKeyDown.bind(this));
        this.addEventListener('paste', this.onPaste);
        this.businessMobileWrapper = this.closest('.js-business-mobile-quantity-card');

        bindQuantityButtons(this, {
            onAdjust: this.decreaseOrIncrease.bind(this)
        });
    }

    /**
     * @function toggleIncreaseAndDecreaseButtons
     * @description Toggles the increase and decrease button state based on the string passed in. This is so that
     * values are not changeable while a network call is happening.
     * @param {string} disableOrEnable - Expects 'disable' or 'enable'.
     */
    toggleIncreaseAndDecreaseButtons(disableOrEnable) {
        toggleQuantityButtons(this, disableOrEnable);
    }

    onBlur(evt) {
        if (evt.target.value.length === 0) {
            evt.target.value = '0';
        }

        // Clamp only when the user leaves the field — clamping on each keystroke
        // turns "1000" into "999" as soon as the 4th digit is typed.
        this.validateMinMax();
        this.debouncedChange();
    }

    onInput(evt) {
        if (evt.target.value.length === 0) return;

        const field = this.quantityField;
        const value = parseInt(field.value, 10);
        const max = field.max !== '' ? parseInt(field.max, 10) : NaN;
        const min = field.min !== '' ? parseInt(field.min, 10) : NaN;
        const isExceedingMax = !Number.isNaN(value) && !Number.isNaN(max) && value > max;
        const isBelowMin = !Number.isNaN(value) && !Number.isNaN(min) && value < min;

        // While the value is outside min/max, wait for blur so the user can finish typing
        // (e.g. "1000" or typing "15" when min is 5).
        if (isExceedingMax || isBelowMin) {
            this.debouncedChange.cancel();
            return;
        }

        this.debouncedChange();
    }

    onKeyDown(evt) {
        if (this.forbiddenValues.indexOf(evt.key || evt.keyCode) !== -1) {
            evt.preventDefault();
        }
    }

    onPaste(evt) {
        // Replace all non-digits on copy paste
        evt.preventDefault();
        const newValue = evt.clipboardData.getData('text').replace(/\D/g, '');
        evt.target.value = ''; // required, otherwise it won't remove the dot
        evt.target.value = newValue;

        this.validateMinMax();
        this.debouncedChange();
    }

    decreaseOrIncrease({ event, delta }) {
        const field = this.quantityField;
        const currentValue = parseQuantity(field.value, parseQuantity(field.min, 0));
        const decrease = delta < 0;
        const manuallyTriggered = event.isTrusted;

        // Show a confirmation modal when the quantity is manually decreased to 0, and the confirmation modal is enabled.
        if (this.confirmRemove && decrease && currentValue === 1 && manuallyTriggered) {
            const confirmationModal = new VZConfirmationModal();
            confirmationModal.toggleConfirmationModal().then((confirmed) => {
                if (confirmed) {
                    this.updateQuantity(field, 0);
                }
            });
        } else {
            this.updateQuantity(field, currentValue + delta);
        }
    }

    updateQuantity(field, newValue) {
        if (window.isNaN(newValue)) {
            field.value = field.min;
        } else {
            field.value = newValue;
        }

        this.validateMinMax();
        this.debouncedChange();

        // Highlight info card if applicable
        if (this.quantityField.classList.contains('js-highlight-container-when-not-empty')) this.highlightInfoCard(field.value !== '0');
    }

    highlightInfoCard(highlight) {
        const infoCard = this.quantityField.closest('.js-info-card');

        if (infoCard) infoCard.classList.toggle('info-card--bordered-primary', highlight);
    }

    validateMinMax() {
        const field = this.quantityField;
        const moreThanMax = parseInt(field.value, 10) > field.max;
        const lessThanMin = parseInt(field.value, 10) < field.min;

        if (field.max && moreThanMax) field.value = field.max;
        if (lessThanMin) {
            field.value = field.min;

            // Display an alert when an attempt is made to reduce the quantity below the existing number of phones on Business Mobile"
            if (field.value > 1 && this.businessMobileWrapper) {
                if (this.businessMobileWrapper.innerHTML.includes('alert')) {
                    Animation.shakeSubtle(this.businessMobileWrapper.querySelector('.alert'));
                } else {
                    const alertTitle = this.businessMobileWrapper.getAttribute('alert-title');
                    const alertDescription = this.businessMobileWrapper.getAttribute('alert-desc');
                    const alert = new Alert(this.businessMobileWrapper.querySelector('.js-quantity-selector-wrapper'));
                    alert.show(alertTitle, alertDescription, 'danger', 'mt-2');

                    // Scroll to BM Devices
                    const addDevicesLink = document.querySelector('.js-add-devices-link');
                    const devicesContainer = document.querySelector('.js-hardware-device-cards');
                    if (addDevicesLink && devicesContainer) {
                        addDevicesLink.addEventListener('click', (event) => {
                            event.preventDefault();
                            scrollToElement(devicesContainer);
                        });
                    }
                }
            }
        }
    }

    onChange() {
        const field = this.quantityField;

        // Enforce bounds before committing (covers typed values after debounce)
        this.validateMinMax();

        // Double check to see if there has been an actual change in value
        if (field.value === this.quantityFieldOrgValue) return;

        this.quantityFieldOrgValue = field.value;
        field.setAttribute('value', field.value);
        field.dispatchEvent(
            new CustomEvent('fieldUpdated', {
                detail: {
                    id: Date.now(),
                    value: field.value
                }
            })
        );

        if (this.quantityField.value !== '0') {
            this.formControl.classList.add('border-turquoise');
        }

        const vZProductCard = this.closest(PRODUCT_CARD_SELECTORS);
        const urlAttributeValue = this.quantityField.getAttribute('data-url');
        if (!urlAttributeValue || !vZProductCard) {
            return;
        }

        let fieldUrl = getUrlFromAttributes(urlAttributeValue, vZProductCard, this.excludedAttributes);
        fieldUrl = removeParamFromURL(fieldUrl, 'quantity');
        fieldUrl = appendParamToURL(fieldUrl, 'quantity', this.productQuantityField.value);

        if (vZProductCard.getAttribute('data-configurator-type') === 'business-ict') {
            let businessRulesProducts = vZProductCard.getAttribute('businessrules-products');

            const backupIncludeInput = vZProductCard.querySelector('input[name="managed-secure-backup"]:not(#managed-secure-backup-none)');
            const selectedBackupInput = vZProductCard.querySelector('input[name="managed-secure-backup"]:checked');
            const currentBackupProductId = backupIncludeInput ? backupIncludeInput.id : null;

            // Only mirror qty when the current tier state still includes backup.
            const shouldSyncBackupQty = currentBackupProductId && selectedBackupInput?.id === currentBackupProductId;

            businessRulesProducts = businessRulesProducts && JSON.parse(businessRulesProducts);
            if (businessRulesProducts && shouldSyncBackupQty && businessRulesProducts[currentBackupProductId]) {
                businessRulesProducts[currentBackupProductId].qty = this.productQuantityField.value;
                fieldUrl = appendParamToURL(fieldUrl, 'businessrulesproducts', JSON.stringify(businessRulesProducts));
            }
        }

        // Disable the buttons - Has to be before the return statement to work for one net quantity selectors.
        this.toggleIncreaseAndDecreaseButtons('disable');

        if (!fieldUrl) return;

        if (this.editMode) {
            const pliId = getParamFromURL(window.location.href, 'pliId');
            fieldUrl = appendParamToURL(fieldUrl, 'pliId', pliId);
        }

        this.spinner.start();

        fetch(fieldUrl)
            .then((res) => res.text())
            .then((response) => {
                vZProductCard.dispatchEvent(new CustomEvent('variationChange', { detail: { response: response } }));
            })
            .finally(() => {
                this.toggleIncreaseAndDecreaseButtons('enable'); // Enable buttons
                this.spinner.stop();
            });
    }
}

if (!window.customElements.get('vz-quantity-selector')) {
    window.customElements.define('vz-quantity-selector', VZQuantitySelector);
}
